import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { initials } from '@repo/shared';
import { Icon, InitialsAvatar } from '@repo/ui';
import { useTenant } from '../../app/tenant';
import { AccountDialog } from '../../auth/AccountDialog';
import { useLogout, useMe } from '../../auth/session';
import { SuspenseOutlet } from '../../routes/lazy';

/**
 * Platform console shell (admin.<domain>). Own module: the router imports it eagerly, while the pages
 * (DataGrid, date pickers) stay in lazy chunks. Same top-bar language as the school shell: product, scope
 * label, account menu (« Mon compte », logout).
 */
export function PlatformShell() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [account, setAccount] = useState(false);
  const path = useRouterState({ select: (st) => st.location.pathname });
  const section = path.startsWith('/platform/journal') ? '/platform/journal' : '/platform';
  useEffect(() => {
    document.title = t('platform.documentTitle', { product: tenant.productName });
  }, [t, tenant.productName]);
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box
        component="header"
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, md: 3 },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box component="img" src="/icon.svg" alt="" sx={{ width: 32, height: 32, borderRadius: '8px' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: '18px' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {tenant.productName}
          </Box>
          <Box component="span" sx={{ fontSize: 12, color: 'text.tertiary' }}>
            {t('platform.scope')}
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        <ButtonBase
          onClick={(e) => setMenu(e.currentTarget)}
          aria-label={t('shell.account')}
          aria-haspopup="menu"
          sx={{ borderRadius: '20px', p: 0.5, gap: 1, minWidth: 0, '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-end',
              minWidth: 0,
              lineHeight: '16px',
              paddingInlineStart: 1,
            }}
          >
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }} dir="auto">
              {me.displayName}
            </Box>
            <Box component="span" sx={{ fontSize: 12, color: 'text.tertiary' }}>
              {t('roles.SUPER_ADMIN')}
            </Box>
          </Box>
          <InitialsAvatar initials={initials(`${me.firstName} ${me.lastName}`)} size={36} />
        </ButtonBase>
      </Box>
      {/* The console's own sections (school pages such as /parametres live on each school's address). */}
      <Box
        component="nav"
        aria-label={t('platform.nav.label')}
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', px: { xs: 1, md: 2 } }}
      >
        <Tabs value={section} onChange={(_e, to: string) => void navigate({ to })} variant="scrollable">
          <Tab
            value="/platform"
            label={t('platform.nav.schools')}
            icon={<Icon name="domain" size={20} />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab
            value="/platform/journal"
            label={t('platform.nav.journal')}
            icon={<Icon name="policy" size={20} />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>
      <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
        <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
          <Typography variant="subtitle2" dir="auto">
            {me.displayName}
          </Typography>
          <Typography
            variant="caption"
            dir="ltr"
            sx={{
              color: 'text.tertiary',
              unicodeBidi: 'isolate',
              overflowWrap: 'anywhere',
              display: 'block',
            }}
          >
            {me.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setMenu(null);
            setAccount(true);
          }}
        >
          <ListItemIcon>
            <Icon name="manage_accounts" />
          </ListItemIcon>
          <ListItemText>{t('account.menu')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenu(null);
            logout.mutate(undefined, { onSettled: () => void navigate({ to: '/login' }) });
          }}
        >
          <ListItemIcon>
            <Icon name="logout" mirrorInRtl />
          </ListItemIcon>
          <ListItemText>{t('common.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      {account && <AccountDialog onClose={() => setAccount(false)} />}
      <SuspenseOutlet />
    </Box>
  );
}
